<template>
    <nav class="global-nav">
        <div v-if="showTitle" class="section-title">Навигация</div>
        <div class="items">
            <a
                href="https://automarketolog.ru/"
                target="_blank"
                rel="noopener noreferrer"
                class="nav-item"
            >
                <ChatIcon name="layers" :size="14" />
                <span class="nav-title">СМИ</span>
                <span class="nav-ext" aria-hidden="true">↗</span>
            </a>

            <router-link
                to="/analytics"
                class="nav-item"
                active-class="nav-item--active"
            >
                <ChatIcon name="trending-up" :size="14" />
                <span class="nav-title">Аналитика</span>
            </router-link>

            <div class="content-wrap">
                <button
                    type="button"
                    class="nav-item nav-item--button"
                    :class="{ 'nav-item--active': isContentRouteActive }"
                    :disabled="!isAuthenticated"
                    @click="toggleContent"
                >
                    <ChatIcon name="folder" :size="14" />
                    <span class="nav-title">Контент</span>
                    <span class="caret" :class="{ 'caret--open': isContentOpen }" />
                </button>

                <div v-if="isContentOpen" class="content-dropdown">
                    <div v-if="isLoading" class="content-state">Загрузка...</div>
                    <div v-else-if="error" class="content-state content-state--error">{{ error }}</div>
                    <div v-else-if="!contentPages.length" class="content-state">Нет страниц</div>
                    <template v-else>
                        <router-link
                            v-for="page in contentPages"
                            :key="page.id"
                            :to="{ name: 'Content', params: { slug: page.slug } }"
                            class="content-link"
                            :class="{ 'content-link--active': route.path === `/content/${page.slug}` }"
                            @click="closeContent"
                        >
                            {{ page.title }}
                        </router-link>
                    </template>
                </div>
            </div>

            <button type="button" class="nav-item nav-item--disabled" disabled>
                <ChatIcon name="search" :size="14" />
                <span class="nav-title">Поиск по VIN</span>
                <span class="badge-soon">скоро</span>
            </button>

            <button type="button" class="nav-item nav-item--disabled" disabled>
                <ChatIcon name="search" :size="14" />
                <span class="nav-title">ИНН поиск</span>
                <span class="badge-soon">скоро</span>
            </button>
        </div>

        <div v-if="lastUpdate" class="last-update">{{ lastUpdate }}</div>
    </nav>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { fetchWordPressPages } from '@/utils/requests'

const props = defineProps({
    isAuthenticated: { type: Boolean, default: false },
    lastUpdate: { type: String, default: '' },
    showTitle: { type: Boolean, default: true },
})

const route = useRoute()
const isContentOpen = ref(false)
const isLoading = ref(false)
const error = ref('')
const contentPages = ref([])

const isContentRouteActive = computed(() => route.path.startsWith('/content'))

async function loadContentPages() {
    if (isLoading.value) return
    isLoading.value = true
    error.value = ''
    try {
        const response = await fetchWordPressPages()
        const payload = await response.json().catch(() => ({}))
        if (!response.ok || payload?.success === false) {
            throw new Error(payload?.data?.message || payload?.message || 'Не удалось загрузить страницы.')
        }
        const data = payload?.data || {}
        contentPages.value = Array.isArray(data.pages) ? data.pages : []
    } catch (err) {
        error.value = err.message || 'Не удалось загрузить страницы.'
        contentPages.value = []
    } finally {
        isLoading.value = false
    }
}

async function toggleContent() {
    if (!props.isAuthenticated) return
    if (isContentOpen.value) {
        isContentOpen.value = false
        return
    }
    isContentOpen.value = true
    if (!contentPages.value.length || error.value) {
        await loadContentPages()
    }
}

function closeContent() {
    isContentOpen.value = false
}

watch(() => route.fullPath, closeContent)
</script>

<style scoped>
.global-nav {
    padding: 0 4px;
}

.section-title {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #888780;
    padding: 0 8px 6px;
}

.items {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-family: inherit;
    font-size: 12.5px;
    color: #23282C;
    text-decoration: none;
    cursor: pointer;
    text-align: left;
}

.nav-item:hover:not(:disabled):not(.nav-item--disabled) {
    background: #F1EFE8;
}

.nav-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-item--active {
    color: #198FA0;
    background: rgba(25, 143, 160, 0.08);
    font-weight: 600;
}

.nav-ext {
    color: #888780;
    font-size: 11px;
}

.nav-item--disabled {
    color: #B4B2A9;
    cursor: not-allowed;
}

.badge-soon {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888780;
    background: #F1EFE8;
    padding: 2px 6px;
    border-radius: 8px;
}

.content-wrap {
    display: flex;
    flex-direction: column;
}

.caret {
    width: 6px;
    height: 6px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.16s ease;
    flex-shrink: 0;
    color: #888780;
}

.caret--open {
    transform: rotate(225deg);
}

.content-dropdown {
    display: flex;
    flex-direction: column;
    padding: 2px 0 6px 30px;
    gap: 1px;
}

.content-link {
    font-size: 12px;
    color: #5F5E5A;
    text-decoration: none;
    padding: 5px 8px;
    border-radius: 6px;
}

.content-link:hover {
    background: #F1EFE8;
    color: #23282C;
}

.content-link--active {
    color: #198FA0;
    font-weight: 600;
}

.content-state {
    padding: 6px 8px;
    font-size: 11.5px;
    color: #888780;
}

.content-state--error {
    color: #B83232;
}

.last-update {
    margin-top: 8px;
    padding: 0 8px;
    font-size: 10.5px;
    color: #B4B2A9;
    line-height: 1.4;
}
</style>
